import toNumber from 'lodash/toNumber'
import isEmpty from 'lodash/isEmpty'

import { useHistory } from '@ztechapps/common.hook'
import map from 'lodash/map'
import { MeasureType } from './useSizeFilter'
import { routesDef } from '../../../../config/routes'
import type {
  CategoryQueryFilter,
  HorizontalQueryFilter,
  OnChangeFilter,
  QueryFilter,
  ShapeQueryFilter,
  VerticalQueryFilter,
} from '../../../../config/types'
import { useSearchParams } from 'react-router-dom'

export const useFilter = () => {
  const defaultFilter = { status: 'Active', forSale: true }
  const history = useHistory()

  let filter: any = { status: 'Active', forSale: true }
  const [searchParams] = useSearchParams()
  const rawSearchParams = Object.fromEntries(searchParams) ?? {}

  const categoryFilter = rawSearchParams?.categoryFilter ? rawSearchParams.categoryFilter : null
  const shapesFilter = rawSearchParams?.shapesFilter ? rawSearchParams.shapesFilter.split(',') : null
  const verticalMaxFilter = rawSearchParams?.verticalMaxFilter ? toNumber(rawSearchParams.verticalMaxFilter) : null
  const verticalMinFilter = rawSearchParams?.verticalMinFilter ? toNumber(rawSearchParams.verticalMinFilter) : null
  const verticalMidFilter = rawSearchParams?.verticalMidFilter ? toNumber(rawSearchParams.verticalMidFilter) : null
  const verticalMeasureType = rawSearchParams?.verticalMeasureType
    ? (rawSearchParams.verticalMeasureType as MeasureType)
    : null
  const horizontalMaxFilter = rawSearchParams?.horizontalMaxFilter
    ? toNumber(rawSearchParams.horizontalMaxFilter)
    : null
  const horizontalMinFilter = rawSearchParams?.horizontalMinFilter
    ? toNumber(rawSearchParams.horizontalMinFilter)
    : null
  const horizontalMidFilter = rawSearchParams?.horizontalMidFilter
    ? toNumber(rawSearchParams.horizontalMidFilter)
    : null
  const horizontalMeasureType = rawSearchParams?.horizontalMeasureType
    ? (rawSearchParams.horizontalMeasureType as MeasureType)
    : null
  const priceFilter = rawSearchParams?.priceFilter ? toNumber(rawSearchParams.priceFilter) : null

  const all: any = []
  //build for category filter, this is single value only so just provide it
  if (!isEmpty(categoryFilter)) {
    let allCategories: any = [{ key: 'category', value: categoryFilter }]
    allCategories = {
      metadata: {
        $elemMatch: {
          $or: allCategories,
        },
      },
    }
    all.push(allCategories)
  }

  //build for multiple for shape filter
  if (!isEmpty(shapesFilter)) {
    const mappedShapes = map(shapesFilter, (e) => ({ key: 'shape', value: e }))
    const allShapes = {
      metadata: {
        $elemMatch: {
          $or: mappedShapes,
        },
      },
    }
    all.push(allShapes)
  }

  //build for the sizes for the die VERTICAL
  if (!isEmpty(verticalMeasureType)) {
    const maxSize = toNumber(verticalMaxFilter)
    const minSize = toNumber(verticalMinFilter)
    let key = 'verticalMm' //default is millimeter for the key

    //but if the type is inches, set it to that
    if (verticalMeasureType === MeasureType.in) {
      key = 'verticalIn'
    }

    let size: any = {
      metadata: {
        $elemMatch: {
          $and: [{ key }, { value: { $lte: maxSize, $gte: minSize } }],
        },
      },
    }
    if (!verticalMidFilter) {
      size = null
    }
    if (size) {
      all.push(size)
    }
  }

  //build for the sizes for the die HORIZONTAL
  if (!isEmpty(horizontalMeasureType)) {
    const maxSize = toNumber(horizontalMaxFilter)
    const minSize = toNumber(horizontalMinFilter)
    let key = 'horizontalMm' //default is millimeter for the key

    //but if the type is inches, set it to that
    if (horizontalMeasureType === MeasureType.in) {
      key = 'horizontalIn'
    }

    let size: any = {
      metadata: {
        $elemMatch: {
          $and: [{ key }, { value: { $lte: maxSize, $gte: minSize } }],
        },
      },
    }
    if (!horizontalMidFilter) {
      size = null
    }

    if (size) {
      all.push(size)
    }
  }

  if (!isEmpty(all)) {
    filter = {
      $and: all,
      ...filter,
    }
  }

  const onChangeFilter: OnChangeFilter = (changedValue: QueryFilter) => {
    const options = {
      priceFilter,
      categoryFilter,
      shapesFilter,
      verticalMeasureType,
      verticalMaxFilter,
      verticalMinFilter,
      verticalMidFilter,
      horizontalMeasureType,
      horizontalMaxFilter,
      horizontalMinFilter,
      horizontalMidFilter,
      ...changedValue,
    }

    const url = routesDef.ProductListPage.altPath(options)
    history.push(url)
  }

  const allFilter = {
    priceFilter,
    categoryFilter,
    shapesFilter,
    verticalMeasureType,
    verticalMaxFilter,
    verticalMinFilter,
    verticalMidFilter,
    horizontalMeasureType,
    horizontalMaxFilter,
    horizontalMinFilter,
    horizontalMidFilter,
  }

  //RESET OF FILTERS
  const resetFilter: {
    shapes: {
      value: ShapeQueryFilter
      hasValue: boolean
    }
    category: {
      value: CategoryQueryFilter
      hasValue: boolean
    }
    verticalSize: {
      value: VerticalQueryFilter
      hasValue: boolean
    }
    horizontalSize: {
      value: HorizontalQueryFilter
      hasValue: boolean
    }
  } = {
    shapes: {
      value: {
        shapesFilter: null,
      },
      hasValue: !isEmpty(shapesFilter),
    },
    category: {
      value: {
        categoryFilter: null,
      },
      hasValue: !isEmpty(categoryFilter),
    },
    verticalSize: {
      value: {
        verticalMaxFilter: 0,
        verticalMinFilter: 0,
        verticalMidFilter: null,
        verticalMeasureType: null,
      },
      hasValue: !!verticalMidFilter,
    },
    horizontalSize: {
      value: {
        horizontalMaxFilter: 0,
        horizontalMinFilter: 0,
        horizontalMidFilter: null,
        horizontalMeasureType: null,
      },
      hasValue: !!horizontalMidFilter,
    },
  }

  return {
    filter,
    ...allFilter,
    allFilter,

    defaultFilter,
    onChangeFilter,
    resetFilter,
  }
}
